import { Button } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { PERMISSIONS } from "@/constants/permissions";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { FlowJsonDialog } from "./components/flow-json-dialog";
import { FlowTimeline } from "./components/flow-timeline";
import { WeekPreview } from "./components/week-preview";
import { DEFAULT_LEAD_CONTACT_FLOW } from "./constants/default-lead-contact-flow";
import type { LeadContactFlow } from "./types/lead-contact-flow.types";
import { addFlowComponent, moveFlowNode } from "./utils/add-flow-component";
import { readStoredLeadContactFlow, saveLeadContactFlow } from "./utils/lead-contact-flow-json";
import { parseLeadAt, projectLeadContactFlow } from "./utils/project-lead-contact-flow";

const LeadContactFlowSearchSchema = z.object({
    leadAt: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_LEAD_CONTACT_FLOW)({
    component: LeadContactFlowPage,
    validateSearch: zodValidator(LeadContactFlowSearchSchema),
    staticData: {
        config: {
            title: "developer_lead_contact_flow_title",
            breadcrumb: {
                title: "developer_lead_contact_flow_title",
                to: APP_ROUTES_TO.DEVELOPER_LEAD_CONTACT_FLOW,
            },
        },
        permissions: [PERMISSIONS.CALL_SVC_ADMIN_BASE],
    },
});

function LeadContactFlowPage() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });
    const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [flow, setFlow] = useState<LeadContactFlow>(readStoredLeadContactFlow);
    const leadAt = useMemo(() => parseLeadAt(search.leadAt, DateTime.now()), [search.leadAt]);
    const steps = useMemo(() => projectLeadContactFlow(flow, leadAt), [flow, leadAt]);
    const openEditor = useCallback(() => setEditorOpen(true), []);

    const handleLeadAtChange = useCallback(
        (next: DateTime) => {
            navigate({
                to: Route.to,
                search: { leadAt: next.toFormat("yyyy-MM-dd'T'HH:mm") },
                replace: true,
            });
        },
        [navigate]
    );

    return (
        <div className="flex flex-col gap-4 p-4">
            <section className="overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br from-card via-card to-primary/[0.05] shadow-sm">
                <div className="flex items-end justify-between gap-3 border-b border-border/70 px-4 py-3">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                            {t("lead_contact_flow_default")}
                        </p>
                        <h1 className="text-lg font-semibold tracking-tight">
                            {t("developer_lead_contact_flow_title")}
                        </h1>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={openEditor}>
                            {t("lead_contact_flow_edit_json")}
                        </Button>
                        <p className="max-w-md text-right text-xs text-muted-foreground">
                            {t("developer_lead_contact_flow_description")}
                        </p>
                    </div>
                </div>
                <FlowTimeline
                    steps={steps}
                    activeNodeId={activeNodeId}
                    onActiveNodeId={setActiveNodeId}
                    onEdit={openEditor}
                    onAdd={(action, columnId) => {
                        const next = addFlowComponent(flow, action, columnId);
                        setFlow(next);
                        saveLeadContactFlow(next);
                    }}
                    onMove={(nodeId, columnId) => {
                        const next = moveFlowNode(flow, nodeId, columnId);
                        setFlow(next);
                        saveLeadContactFlow(next);
                    }}
                />
            </section>

            <WeekPreview
                leadAt={leadAt}
                steps={steps}
                activeNodeId={activeNodeId}
                onActiveNodeId={setActiveNodeId}
                onLeadAtChange={handleLeadAtChange}
                onEdit={openEditor}
            />

            <FlowJsonDialog
                open={editorOpen}
                flow={flow}
                onOpenChange={setEditorOpen}
                onApply={(next) => {
                    setFlow(next);
                    saveLeadContactFlow(next);
                }}
                onReset={() => {
                    setFlow(DEFAULT_LEAD_CONTACT_FLOW);
                    saveLeadContactFlow(DEFAULT_LEAD_CONTACT_FLOW);
                }}
            />
        </div>
    );
}
